import { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft, Crown, Lock, Sparkles } from "lucide-react";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import type { PlanName } from "@/hooks/use-subscription";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface RequirePlanProps {
  /** Plano mínimo exigido */
  tier: Exclude<PlanName, "free">;
  /** Nome legível do recurso (ex: "Alertas por e-mail") */
  featureName: string;
  /** Descrição opcional exibida na tela de upgrade */
  description?: string;
  /**
   * Se `redirect` for true, redireciona para /planos em vez de renderizar
   * a tela de upgrade. Útil para fluxos críticos.
   */
  redirect?: boolean;
  children: ReactNode;
}

const TIER_LABEL: Record<Exclude<PlanName, "free">, string> = {
  pro: "Pro",
  premium: "Premium",
};

const TIER_BENEFITS: Record<Exclude<PlanName, "free">, string[]> = {
  pro: [
    "Até 100 jogos salvos",
    "Estatísticas avançadas, DNA do jogo e gerador inteligente",
    "Combinações e fechamentos estratégicos",
    "Alertas de resultado por e-mail",
  ],
  premium: [
    "Coach IA — chat analítico com seu histórico",
    "Gerador IA personalizado com justificativa",
    "Análise IA de qualquer jogo",
    "Bolões otimizados + jogos e bolões ilimitados",
  ],
};

/**
 * Guard de ROTA por plano. Diferente do `PlanGate` (que envolve um pedaço de
 * UI), este componente é usado no roteador para bloquear o acesso direto via
 * URL a páginas pagas. Mostra uma tela de upgrade contextual que indica o
 * plano necessário, o recurso solicitado, e propaga a rota original para
 * o checkout — depois do pagamento o usuário volta direto pra onde tentou ir.
 */
export function RequirePlan({ tier, featureName, description, redirect, children }: RequirePlanProps) {
  const { has, loading } = usePlanLimits();
  const location = useLocation();
  const fromPath = `${location.pathname}${location.search}${location.hash}`;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Carregando…
      </div>
    );
  }

  if (!has(tier)) {
    const params = new URLSearchParams({
      from: fromPath,
      tier,
      feature: featureName,
    });
    const planosHref = `/planos?${params.toString()}`;

    if (redirect) {
      return <Navigate to={planosHref} state={{ from: location, requiredTier: tier }} replace />;
    }

    const Icon = tier === "premium" ? Crown : Sparkles;
    const benefits = TIER_BENEFITS[tier];

    return (
      <div className="px-4 py-8 sm:px-6 lg:px-10">
        <Card className="mx-auto max-w-2xl border-lt-border bg-lt-bg2">
          <CardContent className="flex flex-col items-center gap-6 px-6 py-12 text-center sm:px-12 sm:py-16">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <Lock className="h-7 w-7 text-primary" />
            </div>

            <div className="space-y-3">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-primary">
                <Icon className="h-3 w-3" />
                Requer plano {TIER_LABEL[tier]}
              </div>
              <h2 className="font-serif text-2xl font-light text-foreground sm:text-3xl">
                Desbloqueie <em className="italic text-primary">{featureName}</em>
              </h2>
              {description && (
                <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
                  {description}
                </p>
              )}
            </div>

            <ul className="w-full max-w-sm space-y-2 text-left text-[13px] text-foreground">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-muted-foreground">
              7 dias grátis · cancele quando quiser · você volta direto pra esta página
            </p>

            <div className="flex w-full flex-col items-center gap-2 sm:w-auto sm:flex-row">
              <Button asChild size="lg" className="w-full font-medium sm:w-auto">
                <Link to={planosHref}>Conhecer o {TIER_LABEL[tier]}</Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="w-full sm:w-auto">
                <Link to="/app">
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Voltar ao painel
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}